import React from "react";

import { ThemeProvider } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import {
  browserTracingIntegration,
  init,
  replayIntegration,
} from "@sentry/react";
// Global Mapbox GL CSS — required by all map components; do not remove (regressed in PR #2264)
import "mapbox-gl/dist/mapbox-gl.css";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import App from "./App";
import { PostHogProviderWrapper } from "./components/PostHogProviderWrapper";
import { AuthProvider } from "./context";
import "./index.css";
import reportWebVitals from "./reportWebVitals";
import { theme } from "./theme";

const DEV_API_URL =
  "https://3xje7yxnal.execute-api.us-west-2.amazonaws.com/development-env";
const PROD_API_URL =
  "https://1ba9u0gj2h.execute-api.us-west-2.amazonaws.com/prod-env";
const SENTRY_DSN = import.meta.env.REACT_APP_FRONTEND_SENTRY_DSN;

if (SENTRY_DSN && SENTRY_DSN.trim() !== "") {
  init({
    dsn: SENTRY_DSN,
    integrations: [
      browserTracingIntegration(),
      replayIntegration({
        networkDetailAllowUrls: [".*"],
        maskAllText: false,
        blockAllMedia: false,
        block: ["sentry-block"],
      }),
    ],
    tracesSampleRate: 0.4,
    tracePropagationTargets: [DEV_API_URL, PROD_API_URL],
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 0.9,
  });
}

// Get the root element
const container = document.getElementById("root");

if (!container) {
  throw new Error("Failed to find the root element");
}

// Create a root
const root = createRoot(container);

// Initial render
root.render(
  <React.StrictMode>
    <PostHogProviderWrapper>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <AuthProvider>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </AuthProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </PostHogProviderWrapper>
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
